import React, { useState, useEffect } from 'react';
import { Link, Routes, Route, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, db } from '../utils/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import './AdminPanel.css';

import AdminNavbar from './AdminNavbar';
import AdminProductList from './AdminProductList';
import ProductForm from './ProductForm';
import UserForm from './UserForm';
import AdminPurchases from './AdminPurchases';
import OfferForm from './OfferForm';
import OfferList from './OfferList';
import OfferNotificationManager from './OfferNotificationManager';
import ClientList from './ClientList';
import UploadProductExcel from './UploadProductExcel';
import DownloadProductCSV from './DownloadProductCSV';
import ManageCategories from './ManageCategories';

function AdminPanel() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserPermissions(data.permissions || []);
          setUserRole(data.role || '');
        }
      }
    };

    fetchUserData();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('authToken');
      navigate('/admin/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // Si es admin, siempre true; si no, valida permisos
  const hasPermission = (perm) => userRole === 'admin' || userPermissions.includes(perm);
  
  // Para categorías: solo admin
  const canManageCategories = () => userRole === 'admin';

  return (
    <div>
      <AdminNavbar toggleMenu={toggleMenu} />
      <div className={`admin-menu ${isMenuOpen ? 'open' : ''}`}>
        <div className="admin-menu-content">
        <button className="close-menu" onClick={toggleMenu}>
          &times;
        </button>
        
        <ul>
          {hasPermission('createProduct') && (
            <li><Link to="/admin/ProductForm" onClick={toggleMenu}>Agregar Producto</Link></li>
          )}
          {hasPermission('viewProducts') && (
            <li><Link to="/admin/products" onClick={toggleMenu}>Lista de Productos</Link></li>
          )}
          {hasPermission('createOffer') && (
            <li><Link to="/admin/OfferForm" onClick={toggleMenu}>Agregar Combo</Link></li>
          )}
          {hasPermission('manageOffers') && (
            <li><Link to="/admin/offers" onClick={toggleMenu}>Lista de Combos</Link></li>
          )}
          {hasPermission('createUser') && (
            <li><Link to="/admin/create-user" onClick={toggleMenu}>Crear Usuario</Link></li>
          )}
          {hasPermission('viewEmployees') && (
            <li><Link to="/admin/employees" onClick={toggleMenu}>Empleados</Link></li>
          )}
          {hasPermission('viewClients') && (
            <li><Link to="/admin/clients" onClick={toggleMenu}>Clientes</Link></li>
          )}
          {hasPermission('viewPurchases') && (
            <li><Link to="/admin/purchases" onClick={toggleMenu}>Pedidos</Link></li>
          )}
          {hasPermission('viewOfferNotifications') && (
            <li><Link to="/admin/offer-notifications" onClick={toggleMenu}>Enviar notificación</Link></li>
          )}
          {hasPermission('UploadProductExcel') && (
            <li><Link to="/admin/UploadProductExcel" onClick={toggleMenu}>Subir Excel</Link></li>
          )}
          {hasPermission('DownloadProductCSV') && (
            <li><Link to="/admin/DownloadProductCSV" onClick={toggleMenu}>Descargar Excel</Link></li>
          )}
          {canManageCategories() && (
            <li><Link to="/admin/categories" onClick={toggleMenu}>Gestionar Categorías</Link></li>
          )}
          
          <li><Link to="/" onClick={toggleMenu}>Ir a Tienda</Link></li>
          <li><button onClick={handleLogout}>Cerrar Sesión</button></li>
        </ul>
        </div>
      </div>

      <div className="admin-panel">
        <h2>Panel de Administración</h2>
        <div className="admin-actions">
          {hasPermission('createProduct') && (
            <div className="admin-action">
              <Link to="/admin/ProductForm">
                <i className="fas fa-plus"></i>
                <span>Agregar Producto</span>
              </Link>
            </div>
          )}
          {hasPermission('createOffer') && (
            <div className="admin-action">
              <Link to="/admin/OfferForm">
                <i className="fa-solid fa-box"></i>
                <span>Agregar Combo</span>
              </Link>
            </div>
          )}
          {hasPermission('viewProducts') && (
            <div className="admin-action">
              <Link to="/admin/products">
                <i className="fas fa-list"></i>
                <span>Lista de Productos</span>
              </Link>
            </div>
          )}
          {hasPermission('manageOffers') && (
            <div className="admin-action">
              <Link to="/admin/offers">
                <i className="fa-solid fa-clipboard"></i>
                <span>Lista de Combos</span>
              </Link>
            </div>
          )}
          {hasPermission('createUser') && (
            <div className="admin-action">
              <Link to="/admin/create-user">
                <i className="fas fa-user-plus"></i>
                <span>Crear Usuario</span>
              </Link>
            </div>
          )}
          {hasPermission('viewEmployees') && (
            <div className="admin-action">
              <Link to="/admin/employees">
                <i className="fa-solid fa-users"></i>
                <span>Empleados</span>
              </Link>
            </div>
          )}
           {hasPermission('viewClients') && (
            <div className="admin-action">
              <Link to="/admin/clients">
                <i className="fa-regular fa-address-book"></i>
                <span>Clientes</span>
              </Link>
            </div>
          )}
          {hasPermission('viewPurchases') && (
            <div className="admin-action">
              <Link to="/admin/purchases">
                <i className="fa-solid fa-people-carry-box"></i>
                <span>Pedidos</span>
              </Link>
            </div>
          )}
          {hasPermission('viewOfferNotifications') && (
            <div className="admin-action">
              <Link to="/admin/offer-notifications">
                <i className="fa-solid fa-bullhorn"></i>
                <span>Enviar notificación</span>
              </Link>
            </div>
          )}
          {hasPermission('DownloadProductCSV') && (
            <div className="admin-action">
              <Link to="/admin/DownloadProductCSV">
                <i className="fa-solid fa-file-excel"></i>
                <span>Descargar Excel</span>
              </Link>
            </div>
          )}
          {hasPermission('UploadProductExcel') && (
            <div className="admin-action">
              <Link to="/admin/UploadProductExcel">
                <i className="fa-solid fa-file-excel"></i>
                <span>Subir Excel</span>
              </Link>
            </div>
          )}
          {canManageCategories() && (
            <div className="admin-action">
              <Link to="/admin/categories">
                <i className="fas fa-folder"></i>
                <span>Gestionar Categorías</span>
              </Link>
            </div>
          )}
          <div className="admin-action">
            <Link to="/">
              <i className="fa-solid fa-shop"></i>
              <span>Ir a Tienda</span>
            </Link>
          </div>
        </div>
      </div>

      <Routes>
        {hasPermission('createProduct') && <Route path="/admin/ProductForm" element={<ProductForm />} />}
        {hasPermission('viewProducts') && <Route path="/admin/products" element={<AdminProductList />} />}
        {hasPermission('createCombo') && <Route path="/admin/OfferForm" element={<OfferForm />} />}
        {hasPermission('viewCombos') && <Route path="/admin/offers" element={<OfferList />} />}
        {hasPermission('createUser') && <Route path="/admin/create-user" element={<UserForm />} />}
        {hasPermission('viewClients') && <Route path="/admin/clients" element={<ClientList />} />}
        {hasPermission('viewPurchases') && <Route path="/admin/purchases" element={<AdminPurchases />} />}
        {hasPermission('sendOffers') && <Route path="/admin/offer-notifications" element={<OfferNotificationManager />} />}
        {hasPermission('UploadProductExcel') && <Route path="/admin/UploadProductExcel" element={<UploadProductExcel />} />}
        {hasPermission('DownloadProductCSV') && <Route path="/admin/DownloadProductCSV" element={<DownloadProductCSV />} />}
        {canManageCategories() && <Route path="/admin/categories" element={<ManageCategories />} />}
      </Routes>
    </div>
  );
}

export default AdminPanel;







